import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Appointment, Service, BlockedTime, TeamMember } from '../../types';

interface CalendarViewProps {
  appointments: Appointment[];
  services: Service[];
  blockedTimes: BlockedTime[];
  teamMembers?: TeamMember[];
}

interface TimeSlot {
  hour: number;
  minute: number;
  display: string;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  appointments,
  services,
  blockedTimes,
  teamMembers = [],
}) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());

  // Generate time slots from 8:00 to 20:00 (8 AM to 8 PM)
  const timeSlots: TimeSlot[] = useMemo(() => {
    const slots: TimeSlot[] = [];
    for (let hour = 8; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        slots.push({
          hour,
          minute,
          display: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        });
      }
    }
    return slots;
  }, []);

  // Get week days - recalculated when currentWeek changes
  const weekStart = useMemo(() => {
    console.log('Recalculating weekStart for:', currentWeek);
    return startOfWeek(currentWeek, { weekStartsOn: 1 });
  }, [currentWeek]); // Monday
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  
  // Calculate week end for display
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  
  // Format the week range display
  const weekRangeDisplay = useMemo(() => {
    console.log('Formatting week range:', weekStart, 'to', weekEnd);
    return `${format(weekStart, 'dd MMM', { locale: fr })} - ${format(weekEnd, 'dd MMM yyyy', { locale: fr })}`;
  }, [weekStart, weekEnd]);

  const goToPreviousWeek = () => {
    setCurrentWeek(prev => subWeeks(prev, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeek(prev => addWeeks(prev, 1));
  };

  const goToToday = () => {
    setCurrentWeek(new Date());
  };

  // Get service name
  const getServiceName = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : 'Service inconnu';
  };

  // Get team member name
  const getMemberName = (memberId?: string) => {
    if (!memberId) return null;
    const member = teamMembers.find(m => m.id === memberId);
    return member ? member.name : null;
  };

  // Get service duration
  const getServiceDuration = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.duration : 60;
  };

  // Check if a time slot is blocked
  const isTimeSlotBlocked = (date: Date, timeSlot: TimeSlot) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const timeStr = timeSlot.display;
    
    return blockedTimes.some(blocked => {
      if (blocked.date !== dateStr) return false;
      
      const blockStart = blocked.startTime;
      const blockEnd = blocked.endTime;
      
      return timeStr >= blockStart && timeStr < blockEnd;
    });
  };

  // Get appointments for a specific date and time slot
  const getAppointmentsForSlot = (date: Date, timeSlot: TimeSlot) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const timeStr = timeSlot.display;
    
    return appointments.filter(appointment => {
      if (appointment.date !== dateStr) return false;
      if ((appointment as any).status === 'cancelled') return false;

      const appointmentStart = appointment.startTime;
      const duration = getServiceDuration(appointment.serviceId);
      
      // Calculate end time
      const [startHour, startMinute] = appointmentStart.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(startHour, startMinute);
      startDate.setMinutes(startDate.getMinutes() + duration);
      const endTime = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`;
      
      return timeStr >= appointmentStart && timeStr < endTime;
    });
  };

  // Get slot status and color
  const getSlotStatus = (date: Date, timeSlot: TimeSlot) => {
    const appointmentsInSlot = getAppointmentsForSlot(date, timeSlot);
    const isBlocked = isTimeSlotBlocked(date, timeSlot);
    
    if (isBlocked) {
      return { status: 'blocked', color: '#9e9e9e', textColor: '#fff', label: 'Hors service' };
    }
    
    if (appointmentsInSlot.length > 0) {
      // Vérifier si le rendez-vous est terminé (dans le passé)
      const now = new Date();
      const currentDate = format(date, 'yyyy-MM-dd');
      const currentTime = timeSlot.display;
      const slotDateTime = new Date(`${currentDate}T${currentTime}:00`);
      
      if (slotDateTime < now) {
        return { status: 'completed', color: '#4caf50', textColor: '#fff', label: 'Terminé' };
      } else {
        return { status: 'busy', color: '#f44336', textColor: '#fff', label: 'Occupé' };
      }
    }
    
    return { status: 'free', color: '#e8f5e8', textColor: '#2e7d32', label: 'Libre' };
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#495057', fontWeight: 600 }}>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Calendrier
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={goToToday}
            sx={{ textTransform: 'none' }}
          >
            Aujourd'hui
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={goToPreviousWeek} size="small">
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography variant="h6" sx={{ mx: 2, minWidth: 200, textAlign: 'center' }}>
              {weekRangeDisplay}
            </Typography>
            
            <IconButton onClick={goToNextWeek} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip label="Libre" sx={{ bgcolor: '#e8f5e8', color: '#2e7d32' }} size="small" />
        <Chip label="Occupé" sx={{ bgcolor: '#f44336', color: '#fff' }} size="small" />
        <Chip label="Terminé" sx={{ bgcolor: '#4caf50', color: '#fff' }} size="small" />
        <Chip label="Hors service" sx={{ bgcolor: '#9e9e9e', color: '#fff' }} size="small" />
        <Chip label="Annulé (masqué)" sx={{ bgcolor: '#ff9800', color: '#fff' }} size="small" />
      </Box>

      {/* Calendar Grid */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {/* Days Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, 1fr)', bgcolor: '#f8f9fa' }}>
          <Box sx={{ p: 2, borderRight: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#495057' }}>
              Heure
            </Typography>
          </Box>
          {weekDays.map((day, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                borderRight: index < 6 ? '1px solid #dee2e6' : 'none',
                borderBottom: '1px solid #dee2e6',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#495057' }}>
                {format(day, 'EEEE', { locale: fr })}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057' }}>
                {format(day, 'd')}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Time Slots */}
        <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
          {timeSlots.map((timeSlot, timeIndex) => (
            <Box
              key={timeIndex}
              sx={{
                display: 'grid',
                gridTemplateColumns: '80px repeat(7, 1fr)',
                minHeight: '60px'
              }}
            >
              {/* Time Label */}
              <Box
                sx={{
                  p: 1,
                  borderRight: '1px solid #dee2e6',
                  borderBottom: '1px solid #dee2e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fafafa'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#6c757d' }}>
                  {timeSlot.display}
                </Typography>
              </Box>

              {/* Day Slots */}
              {weekDays.map((day, dayIndex) => {
                const slotInfo = getSlotStatus(day, timeSlot);
                const appointmentsInSlot = getAppointmentsForSlot(day, timeSlot);
                
                return (
                  <Box
                    key={dayIndex}
                    sx={{
                      p: 0.5,
                      borderRight: dayIndex < 6 ? '1px solid #dee2e6' : 'none',
                      borderBottom: '1px solid #dee2e6',
                      bgcolor: slotInfo.color,
                      color: slotInfo.textColor,
                      minHeight: '60px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: slotInfo.status === 'free' ? 'pointer' : 'default',
                      '&:hover': slotInfo.status === 'free' ? {
                        bgcolor: '#d4edda'
                      } : {}
                    }}
                  >
                    {appointmentsInSlot.length > 0 ? (
                      <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                          {appointmentsInSlot[0].clientName || 'Client'}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9, display: 'block' }}>
                          {getServiceName(appointmentsInSlot[0].serviceId)}
                        </Typography>
                        {getMemberName(appointmentsInSlot[0].teamMemberId) && (
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8, display: 'block', fontStyle: 'italic' }}>
                            {getMemberName(appointmentsInSlot[0].teamMemberId)}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {slotInfo.status === 'free' ? '' : slotInfo.label}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default CalendarView;
